import React, { useState, useMemo, useEffect, useCallback } from "react";
import { TreeSelect, Button, Divider, Checkbox, Input, Form, Spin } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import debounce from "lodash/debounce";
import Config from "../../Config";
import { Token } from "../../Utils/helpers/token";

const {
  API: { baseURL },
} = Config;

const { SHOW_PARENT } = TreeSelect;

function highlightMatch(text, search) {
  if (!search) return text;
  const lowerSearch = search.toLowerCase();
  const regex = new RegExp(`(${search})`, "gi");
  return String(text)
    .split(regex)
    .map((part, i) =>
      part.toLowerCase() === lowerSearch ? (
        <span key={i} style={{ backgroundColor: "#ffe58f" }}>
          {part}
        </span>
      ) : (
        part
      )
    );
}

function SelectCustomFilter(props) {
  const [rawSearch, setRawSearch] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [tempValues, setTempValues] = useState([]);
  const [keyToLabelMap, setKeyToLabelMap] = useState({});

  const isSearchActive = search.trim().length > 0;

  const debouncedSearch = useMemo(
    () => debounce((val) => setSearch(val), 300),
    []
  );

  const tempValueKeys = useMemo(
    () => tempValues.map((v) => v.value),
    [tempValues]
  );

  useEffect(() => {
    const controller = new AbortController();

    const getInputData = async () => {
      try {
        const formdata = new FormData();
        formdata.append(
          "authentication",
          Token.encryptAuthParams(window.localStorage.getItem("token"))
        );

        if (
          (props?.client_id && props?.client_id.length !== 0) ||
          (props?.block?.master_dependency &&
            typeof props?.block?.master_dependency === "object" &&
            props?.block?.master_dependency[0]?.id?.includes("Client"))
        ) {
          formdata.append("Client", props.client_id);
        }

        setLoading(true);
        const response = await fetch(baseURL + props?.block?.option_url, {
          method: "POST",
          body: formdata,
          signal: controller.signal,
        });

        const result = await response.json();
        setDataSource(result?.data || []);
      } catch (err) {
        if (err.name !== "AbortError") console.error(err);
      } finally {
        setLoading(false);
      }
    };

    getInputData();
    return () => controller.abort();
  }, [props?.client_id, props?.block?.option_url]);

  useEffect(() => {
    const map = {};
    dataSource.forEach((item) => {
      map[String(item.key)] = item.value;
    });
    setKeyToLabelMap(map);
  }, [dataSource]);

  useEffect(() => {
    debouncedSearch(rawSearch);
  }, [rawSearch]);

  useEffect(() => {
    if (
      isSearchActive &&
      tempValues.some((v) => v.value === String(dataSource[0]?.key))
    ) {
      setTempValues((vals) =>
        vals.filter((v) => v.value !== String(dataSource[0]?.key))
      );
    }
  }, [isSearchActive, dataSource]);

  const filteredData = useMemo(() => {
    return dataSource.filter(
      (c) =>
        (!isSearchActive || String(c.key) !== String(dataSource[0]?.key)) &&
        c.value.toLowerCase().includes(search.toLowerCase())
    );
  }, [search, dataSource, isSearchActive]);

  const filteredKeys = useMemo(
    () => filteredData.map((d) => String(d.key)),
    [filteredData]
  );

  const allSelected =
    filteredKeys.length > 0 &&
    filteredKeys.every((k) => tempValueKeys.includes(k));

  const handleChange = useCallback(
    (newItems) => {
      const cleaned = newItems.filter(
        (item) => item.value !== "__select_all__"
      );

      let nextValues;

      if (cleaned.length === 0) {
        const firstItem = dataSource[0];
        if (firstItem) {
          nextValues = [
            { value: String(firstItem.key), label: firstItem.value },
          ];
        } else {
          nextValues = [];
        }
      } else {
        const justSelected = cleaned.filter(
          (item) => !tempValues.find((t) => t.value === item.value)
        );

        if (
          justSelected.some((item) => item.value === String(dataSource[0]?.key))
        ) {
          nextValues = [
            { value: String(dataSource[0].key), label: dataSource[0].value },
          ];
          setRawSearch("");
          setSearch("");
        } else {
          nextValues =
            cleaned.length > 1
              ? cleaned.filter(
                  (item) => item.value !== String(dataSource[0]?.key)
                )
              : cleaned;
        }
      }

      setTempValues(nextValues);
      props.form.setFieldsValue({
        [props.block.param_name]: nextValues.map((v) => v.value),
      });
    },
    [tempValues, keyToLabelMap, dataSource]
  );

  const handleSelectAllSearchResults = () => {
    const allAlreadySelected = filteredKeys.every((k) =>
      tempValueKeys.includes(k)
    );

    if (allAlreadySelected) {
      const newTempValues = tempValues.filter(
        (v) => !filteredKeys.includes(v.value)
      );
      setTempValues(newTempValues);
      props.form.setFieldsValue({
        [props.block.param_name]: newTempValues.map((v) => v.value),
      });
    } else {
      const withoutDefault = tempValues.filter(
        (v) => v.value !== String(dataSource[0]?.key)
      );
      const newItems = filteredKeys
        .filter((k) => !tempValueKeys.includes(k))
        .map((key) => ({ value: key, label: keyToLabelMap[key] }));

      const updatedValues = [...withoutDefault, ...newItems];
      setTempValues(updatedValues);
      props.form.setFieldsValue({
        [props.block.param_name]: updatedValues.map((v) => v.value),
      });
    }
  };

  const handleClear = () => {
    setTempValues([]);
    props.form.setFieldsValue({
      [props.block.param_name]: [],
    });
    setRawSearch("");
    setSearch("");
  };

  const treeData = useMemo(() => {
    const base = filteredData.map((c) => ({
      title: highlightMatch(c.value, search),
      value: String(c.key),
      label: c.value,
    }));

    const selectAllSearchItem =
      isSearchActive && filteredKeys.length > 0
        ? [
            {
              title: (
                <span
                  className="ant-select-tree-node-content-wrapper"
                  style={{ display: "flex", marginLeft: -8 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <span
                    className="ant-select-tree-title"
                    style={{ fontWeight: "bold" }}
                  >
                    <Checkbox
                      checked={allSelected}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectAllSearchResults();
                      }}
                    >
                      Select All Search Results ({filteredKeys.length})
                    </Checkbox>
                  </span>
                </span>
              ),
              value: "__select_all__",
              className: "custom-select-all-checkbox",
              disableCheckbox: true,
              selectable: false,
            },
          ]
        : [];

    if (isSearchActive && base.length === 0) {
      return [
        {
          title: (
            <span
              className="ant-select-tree-node-content-wrapper"
              style={{ display: "flex", alignItems: "center", paddingLeft: 24 }}
            >
              <span style={{ color: "#262626", fontWeight: "bold" }}>
                No data
              </span>
            </span>
          ),
          value: "__no_data__",
          selectable: false,
          disableCheckbox: true,
          className: "custom-select-all-checkbox",
        },
      ];
    }

    return [...selectAllSearchItem, ...base];
  }, [
    filteredData,
    search,
    keyToLabelMap,
    filteredKeys,
    allSelected,
    isSearchActive,
  ]);

  useEffect(() => {
    const formVal = props.form.getFieldValue(props.block.id) || [];
    const mapped = formVal.map((v) => ({
      value: v,
      label: keyToLabelMap[v] ?? v,
    }));

    if (mapped.length) {
      setTempValues(mapped);
      props.form.setFieldsValue({
        [props.block.param_name]: mapped.map((m) => m.value),
      });
    } else if (dataSource.length > 0) {
      const defaultItem = {
        value: String(dataSource[0].key),
        label: dataSource[0].value,
      };
      setTempValues([defaultItem]);
      props.form.setFieldsValue({
        [props.block.param_name]: [defaultItem.value],
      });
    }
  }, [keyToLabelMap, dataSource]);

  return (
    <div
      className={
        "Report-V2-filter-box select_filter " + (props?.block?.param_name || "")
      }
    >
      <div className="Report-V2-filter-option-title">
        <span>{props?.block?.label}</span>
      </div>
      <Spin spinning={loading}>
        <Form.Item noStyle>
          <TreeSelect
            style={{ width: 260 }}
            value={tempValues}
            treeData={treeData}
            labelInValue
            showSearch={false}
            onChange={handleChange}
            placeholder="Search campaigns"
            multiple
            treeCheckable
            showCheckedStrategy={SHOW_PARENT}
            filterTreeNode={false}
            maxTagCount={1}
            dropdownRender={(menu) => (
              <div>
                <div
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    background: "#fff",
                    padding: "8px 12px",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  <Input
                    allowClear
                    value={rawSearch}
                    onChange={(e) => {
                      const val = e.target.value;
                      setRawSearch(val);
                      if (val === "") {
                        debouncedSearch.cancel();
                        setSearch("");
                      }
                    }}
                    onClear={() => {
                      debouncedSearch.cancel();
                      setRawSearch("");
                      setSearch("");
                    }}
                    placeholder="Search"
                    suffix={<SearchOutlined />}
                  />
                </div>
                {menu}
                <div
                  style={{
                    position: "sticky",
                    bottom: 0,
                    zIndex: 1,
                    background: "#fff",
                    padding: "8px 12px",
                    borderTop: "1px solid #f0f0f0",
                    textAlign: "right",
                  }}
                >
                  <Button
                    size="small"
                    style={{ marginRight: 8 }}
                    onClick={handleClear}
                  >
                    Clear
                  </Button>
                </div>
              </div>
            )}
            onDropdownVisibleChange={(visible) => {
              if (
                !visible &&
                tempValues.length === 0 &&
                dataSource.length > 0
              ) {
                const firstItem = {
                  value: String(dataSource[0].key),
                  label: dataSource[0].value,
                };
                setTempValues([firstItem]);
                props.form.setFieldsValue({
                  [props.block.param_name]: [firstItem.value],
                });
              }
            }}
          />
        </Form.Item>
        <Form.Item name={props.block.param_name} hidden>
          <Input />
        </Form.Item>
      </Spin>
    </div>
  );
}

export default SelectCustomFilter;
